import Lenis from "@studio-freight/lenis";
import barba from '@barba/core';
import gsap from 'gsap';
import CustomEase from 'gsap/CustomEase';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitText from 'gsap/SplitText';

import initHome from "./components/home";
import initPage from "./components/page";

// Import styles
import '../scss/styles.scss';


/* ------------------------------- Lottie init ------------------------------- */
function waitForLottie(maxWaitMs = 5000) {
  if (window.lottie) return Promise.resolve(window.lottie);
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const iv = setInterval(() => {
      if (window.lottie) {
        clearInterval(iv);
        resolve(window.lottie);
      } else if (Date.now() - start > maxWaitMs) {
        clearInterval(iv);
        // Resolve quietly; we don't want to hard-fail the app if lottie isn't ready
        resolve(null);
      }
    }, 50);
  });
}

function initHeaderLottie() {
  const container = document.querySelector('.header__icon');
  if (!container) return;
  if (container.dataset.lottieInited === '1') return; // guard against duplicates

  if (!window.lottie) return; // lottie not ready; caller should await waitForLottie()

  try {
    const anim = window.lottie.loadAnimation({
      container,
      renderer: 'html',
      loop: false,
      autoplay: false,
      path: '/lottie/website_dots.json',
    });
    // keep a reference in case we need it later
    container._lottie = anim;
    container.dataset.lottieInited = '1';
  } catch (e) {
    // no-op; avoid breaking rest of JS
  }
}

gsap.registerPlugin(CustomEase, ScrollTrigger, SplitText);
CustomEase.create("custom", ".42,0,.24,1");

/* --------------------------- fonts readiness --------------------------- */
/* Wait for webfonts before starting any animations (header + views). */
function waitForFonts() {
  // Modern browsers support document.fonts.ready; fallback in case it's missing.
  if (document.fonts && typeof document.fonts.ready?.then === 'function') {
    return document.fonts.ready.catch(() => { }); // swallow just in case
  }
  // Minimal fallback: next frame (no-ops if fonts API is unavailable)
  return Promise.resolve();
}
const fontsReady = waitForFonts();

/* ------------------------------ Lenis init ------------------------------ */
window.lenis = new Lenis({
  smoothWheel: true,
});

function raf(time) {
  lenis.raf(time);
  requestAnimationFrame(raf);
}
requestAnimationFrame(raf);

// Keep ScrollTrigger in sync with Lenis
lenis.on("scroll", () => {
  ScrollTrigger.update();
});

lenis.on("scroll", ({ scroll, limit, velocity, direction }) => {
  // reserved: keep for future hooks; do not remove to avoid behavioral changes
});

/* --------------------------- ScrollTrigger utils --------------------------- */
function refreshScrollTriggers() {
  // Double RAF to ensure layout is fully settled before measuring
  requestAnimationFrame(() => requestAnimationFrame(() => ScrollTrigger.refresh()));
}

function killAllScrollTriggers() {
  // Kill all existing triggers to avoid duplicates/leaks across views
  ScrollTrigger.getAll().forEach(t => t.kill(true));
}

/* -------------------------- header subtle intro ------------------------- */
function animateHeader() {
  const el = document.querySelector('.header');
  if (!el) return;

  const mm = gsap.matchMedia();

  mm.add({
    isDesktop: "(min-width: 769px)",
    isMobile: "(max-width: 768px)"
  }, (context) => {
    const { isMobile } = context.conditions;
    const finalWidth = isMobile ? "calc(100% - 4.8rem)" : "62.5rem";
    const settleWidth = isMobile ? "calc(100% - 5rem)" : "61rem";

    gsap.set(el, {
      width: "24rem",
      height: "4.4rem",
      opacity: 0,
      y: -15
    });

    const tl = gsap.timeline({ defaults: { ease: "power2.out" } });

    tl.to(el, {
      opacity: 1,
      width: finalWidth,
      height: "4.9rem",
      duration: 0.5,
      y: 0
    });

    tl.to(el, {
      width: settleWidth,
      height: "4.8rem",
      duration: 0.4,
      ease: "power1.out"
    });

    return () => mm.revert();
  });
}


/* Run header animation only after DOM is ready and fonts are loaded */
function onReady(fn) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fn, { once: true });
  } else {
    fn();
  }
}

onReady(async () => {
  await fontsReady;
  await waitForLottie();
  initHeaderLottie();
  animateHeader();
});

/* ------------------------------- BarbaJS -------------------------------- */
const header = document.querySelector('.header');

barba.init({
  views: [
    {
      namespace: 'home',
      async beforeEnter() {
        lenis.stop();
        killAllScrollTriggers();
      },
      async afterEnter() {
        await fontsReady;
        initHome();
        refreshScrollTriggers();
      }
    },
    {
      namespace: 'page',
      async beforeEnter() {
        lenis.stop();
        killAllScrollTriggers();
      },
      async afterEnter() {
        await fontsReady;
        initPage();
        refreshScrollTriggers();
        lenis.start();
      }
    }
  ],
  transitions: [
    {
      name: 'home-to-page',
      sync: true,
      from: { namespace: ['home'] },
      to: { namespace: ['page'] },
      leave(data) {
        const done = this.async();
        lenis.stop();

        gsap.to(data.current.container.querySelector('.page-wrapper'), {
          y: '-100vh',
          duration: .8,
          ease: "custom",
          onComplete: done
        });
      },
      enter(data) {
        const done = this.async();
        const nextContainer = data.next.container;

        gsap.set(nextContainer, {
          position: 'fixed',
          top: 0,
          height: '100vh',
          width: '100vw',
          overflow: 'hidden',
          zIndex: 10
        });

        gsap.fromTo(nextContainer, { y: '100vh' }, {
          y: '0',
          duration: .8,
          ease: "custom",
          onComplete: () => {
            gsap.set(nextContainer, { clearProps: 'all' });
            done();
            lenis.scrollTo(0, { immediate: true });
            refreshScrollTriggers();
          }
        });
      },
    },
    {
      name: 'page-to-home',
      sync: true,
      from: { namespace: ['page'] },
      to: { namespace: ['home'] },
      leave(data) {
        const done = this.async();
        lenis.stop();

        gsap.to(data.current.container.querySelector('.page-wrapper'), {
          y: '100vh',
          duration: .8,
          ease: "custom",
          onComplete: done
        });
      },
      enter(data) {
        const done = this.async();
        const nextContainer = data.next.container;

        gsap.set(nextContainer, {
          position: 'fixed',
          top: 0,
          height: '100vh',
          width: '100vw',
          overflow: 'hidden',
          zIndex: 10
        });

        gsap.fromTo(nextContainer, { y: '-100vh' }, {
          y: '0',
          duration: .8,
          ease: "custom",
          onComplete: () => {
            gsap.set(nextContainer, { clearProps: 'all' });
            done();
            lenis.scrollTo(0, { immediate: true });
            refreshScrollTriggers();
          }
        });
      },
    }
  ]
});
